/**
 * Job Documents switch.
 *
 * Controls whether order lines offer the Certificate of Conformity and the
 * Despatch Note, generated from imported ERP job records.
 *
 * Ships OFF. These are certified documents that reach a customer, so they are
 * turned on deliberately once the job data behind them has been checked
 * rather than appearing the moment jobs start importing. The backend refuses
 * to generate either document while this is off, so the switch is the real
 * gate and not just UI dressing.
 */
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../ui/card';
import { Button } from '../../ui/button';
import { Label } from '../../ui/label';
import { Switch } from '../../ui/switch';
import { FileBadge, Loader2 } from 'lucide-react';
import { PlatformSettings } from './types';

interface JobDocumentsSectionProps {
  platformSettings: PlatformSettings;
  saving: boolean;
  onPlatformSettingsChange: (settings: PlatformSettings) => void;
  onSave: () => void;
}

export function JobDocumentsSection({
  platformSettings,
  saving,
  onPlatformSettingsChange,
  onSave,
}: JobDocumentsSectionProps) {
  const enabled = platformSettings.jobDocuments;

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <FileBadge className="h-5 w-5" />
          Job Documents
        </CardTitle>
        <CardDescription>
          Paperwork generated from imported ERP job records and shown on order lines.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="flex items-start justify-between gap-4 p-4 border rounded-lg">
          <div className="space-y-1">
            <Label htmlFor="job-documents-switch" className="text-base font-medium">
              Certificate of Conformity, Despatch Note and FAIR reports
            </Label>
            <p className="text-sm text-muted-foreground">
              When on, order lines with imported jobs gain a Documents tab offering the
              generated certificate and despatch note, and the FAIR report files the ERP
              holds for those jobs. Off by default — these are certified documents sent to
              customers, so switch them on only once the underlying job data has been checked.
            </p>
            <p className="text-sm text-muted-foreground">
              While off, the documents cannot be generated or downloaded at all, not merely hidden.
            </p>
          </div>
          <Switch
            id="job-documents-switch"
            checked={enabled}
            onCheckedChange={(checked) =>
              onPlatformSettingsChange({ ...platformSettings, jobDocuments: checked })
            }
          />
        </div>

        <div className="flex justify-end">
          <Button onClick={onSave} disabled={saving}>
            {saving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            Save
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}
